import { useEffect, useMemo, useState, useCallback } from "react";
import { Card, Empty, Typography, Button } from "antd";
import type { ErrorResponseType, MerchantCabinetWithMerchantsType } from "@src/types";
import MerchantCabinetDetailedCard from "./MerchantCabinetDetailedCard";
import type { AxiosError } from "axios";
import { CustomResult } from "@repo/ui";
import MerchantCabinetCreateDrawer from "./drawers/MerchantCabinetCreateDrawer";

const { Title } = Typography;

export type MerchantCabinetsCardProps = {
    data?: MerchantCabinetWithMerchantsType[];
    loading?: boolean;
    error?: AxiosError<ErrorResponseType> | null;
    initialMerchantId?: string;
};

export default function MerchantCabinetsCard({
    data,
    loading,
    error,
    initialMerchantId,
}: MerchantCabinetsCardProps) {
    if (error) {
        return (
            <CustomResult
                type="error"
                axiosError={error}
                title="Ошибка при получений Магазинов"
                withCard={true}
            />
        );
    }

    const cabinets = data ?? [];

    const [activeKey, setActiveKey] = useState<string>(() => cabinets[0]?.id ?? "");
    const [createOpen, setCreateOpen] = useState(false); // ⬅️ состояние Drawer
    const [initialMerchantApplied, setInitialMerchantApplied] = useState(false);

    const openCreate = useCallback(() => setCreateOpen(true), []);
    const closeCreate = useCallback(() => setCreateOpen(false), []);

    useEffect(() => {
        setInitialMerchantApplied(false);
    }, [initialMerchantId]);

    useEffect(() => {
        if (!cabinets.length) {
            setActiveKey("");
            return;
        }
        if (initialMerchantId && !initialMerchantApplied) {
            const cabinetWithMerchant = cabinets.find((c) =>
                c.merchants.some((merchant) => merchant.id === initialMerchantId),
            );
            if (cabinetWithMerchant) {
                setActiveKey(cabinetWithMerchant.id);
                setInitialMerchantApplied(true);
                return;
            }
        }
        const exists = cabinets.some((c) => c.id === activeKey);
        if (!exists) setActiveKey(cabinets[0]?.id ?? "");
    }, [cabinets, activeKey, initialMerchantId, initialMerchantApplied]);

    const tabList = useMemo(
        () =>
            cabinets.map((c) => ({
                key: c.id,
                tab: (
                    <div className="flex items-center gap-2">
                        <span className="font-medium">{c.username}</span>
                    </div>
                ),
            })),
        [cabinets],
    );

    const contentList = useMemo<Record<string, React.ReactNode>>(() => {
        const map: Record<string, React.ReactNode> = {};
        for (const c of cabinets) {
            map[c.id] = (
                <MerchantCabinetDetailedCard
                    key={c.id}
                    data={c}
                    loading={loading}
                    initialMerchantId={initialMerchantId}
                />
            );
        }
        return map;
    }, [cabinets, loading, initialMerchantId]);

    if (!loading && cabinets.length === 0) {
        return (
            <>
                <CustomResult
                    type="info"
                    title="Вы еще не добавили магазин"
                    withCard={true}
                    extra={
                        <Button type="primary" onClick={openCreate}>
                            Добавить магазин
                        </Button>
                    }
                />
                <MerchantCabinetCreateDrawer open={createOpen} onClose={closeCreate} />
            </>
        );
    }

    return (
        <>
            <Card
                style={{ width: "100%" }}
                title={
                    <div className="flex flex-col">
                        <Title level={4} style={{ margin: 0 }}>
                            Магазины
                        </Title>
                    </div>
                }
                loading={loading}
                tabList={tabList}
                activeTabKey={activeKey}
                onTabChange={setActiveKey}
                tabProps={{ size: "middle" }}
                extra={
                    <Button color="primary" variant="outlined" onClick={openCreate}>
                        Добавить магазин
                    </Button>
                }
            >
                {contentList[activeKey] ?? <Empty description="Выберите кабинет" />}
            </Card>

            <MerchantCabinetCreateDrawer open={createOpen} onClose={closeCreate} />
        </>
    );
}
